import { connect, ConnectedProps } from "react-redux";
import { RootState } from "../store/store"; // Assuming your store file is named 'store.ts'
import { Alert, Avatar, Button, Dropdown, Layout, Space, Switch } from "antd";
import { useEffect, useState, useCallback } from "react";
import BarcodeReader from "react-barcode-reader";

import {
  CloseOutlined,
  UserOutlined,
  LogoutOutlined,
  SunOutlined,
  MoonOutlined,
} from "@ant-design/icons";
import type { MenuProps } from "antd";

import { Outlet, useLocation, useNavigate } from "react-router-dom";

import { isArray } from "lodash";
import CustomDrawer from "../components/hoc/CustomDrawer";

import { Sidebar } from "../layout/Sidebar";
import { fetchData } from "../store/actions/cms";
import { PATH_API_MANAGE } from "../config";
import { useSocket } from "../hooks/useSocket";
import { useMutation, useQuery } from "@tanstack/react-query";
import { queryApi } from "../store/actions/common";
import { queryClient } from "../store/queryClient";
import { emitBarcode, onBarcodeScan } from "../utils/barcodeEvents";
import { userActions } from "../store/slices/user";
import { triggerNotification } from "../store/directActions";
import ModalDashboardSettings from "../components/ModalDashboardSettings";
import { useAppTheme } from "../theme/AppTheme";
import { THEMING_ENABLED } from "../config/dashboardTheme";

interface OwnProps {
  setTheme?: (theme: "light" | "dark") => void;
  theme?: "light" | "dark";
}

type CorePageProps = OwnProps & PropsFromRedux;

type MenuItem = Required<MenuProps>["items"][number];

const fetchScanMetaApi = async (data: any) => {
  return queryApi(`ledger/scan`, "POST", data);
};

const CorePage: React.FC<CorePageProps> = ({
  logout,
  roleType,
  fetchLookup,
  storeId,
  setTheme,
  theme,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const userMenuItems = [
    {
      key: "profile",
      icon: <UserOutlined />,
      label: "Profile",
      onClick: () => navigate("profile"),
    },

    {
      key: "logout",
      icon: <LogoutOutlined />,
      label: "Logout",
      danger: true,
      onClick: logout,
    },
  ];
  const [collapsed, setCollapsed] = useState<boolean>(true);
  // The appearance picker is opened from the sidebar. It lives here rather than
  // in the Sidebar component because Sidebar renders twice (desktop rail and
  // mobile drawer) and there must be only one picker.
  const [showThemeDrawer, setShowThemeDrawer] = useState<boolean>(false);
  const {
    presetKey,
    savedKey,
    mode: colorMode,
    isSaving: isSavingTheme,
    setPreview,
    cancelPreview,
    save: saveTheme,
    reset: resetTheme,
  } = useAppTheme();
  const [loading, setLoading] = useState<boolean>(false);
  const [paymentAlert, setPaymentAlert] = useState<any>();

  const onPay = async () => {
    if (paymentAlert) {
      setLoading(true);
      const data = await queryApi(
        `store/payment/${paymentAlert?.id}`,
        "POST",
        {},
      );
      console.log(data);
      // @ts-ignore
      if (data && data.payment_link) window.location.href = data.payment_link;
      setLoading(false);
    }
  };

  // Fetch payment status using TanStack Query
  const { data: paymentStatus } = useQuery({
    queryKey: ["payment-status", storeId],
    queryFn: () => {
      if (!storeId) return null;
      return queryApi(`store/payment/${storeId}`, "GET");
    },
    enabled: !!storeId,
    staleTime: Infinity, // Always refetch when triggered
    gcTime: 0,
  });

  // Handle store update messages - memoized to prevent re-subscriptions
  const handleStoreUpdate = useCallback((data: any) => {
    if (data?.topic === "get-payment-status") {
      queryClient.invalidateQueries({
        queryKey: ["payment-status"],
      });
    }
    if (data.topic === "client-scan") {
      handleScan(data?.message?.scan_code, "by websocket");
    }
  }, []);

  // Subscribe to store-update topic
  // @ts-ignore
  const { sendMessage } = useSocket({
    rooms: storeId,
    subscriptions: {
      "store-update": handleStoreUpdate,
    },
  });

  const { mutate: fetchScanMeta, isPending: isScanning } = useMutation({
    mutationFn: (code: any) => fetchScanMetaApi(code),
    onSuccess: (scan_meta: any) => {
      if (!scan_meta?.error_code) {
        triggerNotification({
          title: "Success",
          description: "Ticket scanned",
          type: "success",
        });
        queryClient.invalidateQueries({
          queryKey: ["api/dashboard/store/stats"],
        });
        sendMessage("screen-refresh");
      } else {
        triggerNotification({
          title: "Error",
          description: scan_meta.message || "Failed to scan code",
          type: "error",
        });
        if (scan_meta?.error_code === "SCAN230") {
          navigate("/packs", {
            state: {
              openModal: true,
            },
          });
        } else if (scan_meta?.error_code === "SCAN231") {
          navigate("/inventory", {
            state: {
              openModal: true,
            },
          });
        }
      }
    },
    onError: (error) => {
      triggerNotification({
        title: "Error",
        description: error.message || "Failed to scan code",
        type: "error",
      });
    },
  });

  // @ts-ignore
  useEffect(() => {
    return onBarcodeScan(async (data) => {
      if (
        location.pathname !== "/endshift" &&
        location.pathname !== "/startshift" &&
        !(
          ["/packs", "/inventory"].includes(location.pathname) &&
          !!location.state?.openModal
        )
      )
        if (data && data.trim())
          // @ts-ignore
          fetchScanMeta({ scan_code: data });
    });
  }, [location.pathname, location.state?.openModal]);

  useEffect(() => {
    fetchLookup({});
  }, []);

  useEffect(() => {
    if (isArray(paymentStatus)) {
      const pendingPayment = paymentStatus.find(
        (i) => i.payment_status == "pending",
      );
      if (pendingPayment) {
        setPaymentAlert({
          id: pendingPayment.payment_id,
          amount: pendingPayment.amount,
        });
      } else {
        setPaymentAlert(undefined);
      }
    }
  }, [paymentStatus]);

  const handleScan = (code: string, reason: string) => {
    console.log(code, reason);
    emitBarcode(code);
  };

  return (
    <>
      <div
        style={{
          height: "100dvh",
          display: "flex",
        }}
        className="flex-col lg:flex-row app-bg transition-all duration-500"
      >
        <Sidebar
          userRole={roleType}
          setTheme={setTheme}
          theme={theme}
          onProfile={() => navigate("profile")}
          onLogout={logout}
          onAppearance={
            THEMING_ENABLED ? () => setShowThemeDrawer(true) : undefined
          }
        />

        <CustomDrawer
          width={360}
          bodyStyle={{
            // backgroundColor: "#",
            padding: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            rowGap: 32,
            // marginTop: 32,
          }}
          className="bg-gray-800/90"
          onClose={() => setCollapsed(true)}
          placement="left"
          open={!collapsed}
          closeIcon={
            <CloseOutlined
              style={{ color: process.env.REACT_APP_BASE_COLOR, fontSize: 20 }}
            />
          }
        >
          <Sidebar
            userRole={roleType}
            customClass="w-full"
            onProfile={() => {
              setCollapsed(true);
              navigate("profile");
            }}
            onLogout={logout}
            onAppearance={
              THEMING_ENABLED
                ? () => {
                    setCollapsed(true);
                    setShowThemeDrawer(true);
                  }
                : undefined
            }
          />
        </CustomDrawer>
        <div
          className="flex-1 max-w-full lg:max-w-screen-minus-256"
          // style={{ width: "calc(100% - 256px)" }}
        >
          <header className="lg:hidden app-surface backdrop-blur-xl shadow-lg border-b app-border">
            <div className="flex items-center justify-between px-6 py-4">
              <div className="flex items-center space-x-4">
                <svg
                  onClick={() => setCollapsed(false)}
                  x-show="!isOpen"
                  xmlns="http://www.w3.org/2000/svg"
                  className="block lg:hidden h-6 w-6 app-text cursor-pointer"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
                <h2 className="text-lg font-semibold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-white dark:to-gray-200 bg-clip-text text-transparent capitalize">
                  {/* Dashboard */}
                </h2>
              </div>

              <Space size="middle">
                {/* Theme Toggle */}
                {setTheme && (
                  <div className="flex items-center space-x-2">
                    <SunOutlined className="app-muted" />
                    <Switch
                      checked={theme === "dark"}
                      onChange={(checked) =>
                        setTheme(checked ? "dark" : "light")
                      }
                      checkedChildren={<MoonOutlined />}
                      unCheckedChildren={<SunOutlined />}
                    />
                    <MoonOutlined className="app-muted" />
                  </div>
                )}
                {/* Profile */}
                <Dropdown
                  menu={{
                    items: userMenuItems.map((item) => ({
                      ...item,
                      // onClick:
                      // item.key === "profile"
                      // ? () => setCurrentPage("profile")
                      // : item.onClick,
                    })),
                  }}
                  placement="bottomRight"
                >
                  <div className="flex items-center space-x-3 cursor-pointer app-hover rounded-lg p-2 transition-all duration-200">
                    {/* <div className="text-right"> */}
                    {/* <p className="text-sm font-medium app-text">
                        {userData?.name || "John Doe"}
                      </p>
                      <p className="text-xs app-muted capitalize">
                        {userRole} User
                      </p> */}
                    {/* </div> */}
                    <Avatar
                      size="default"
                      icon={<UserOutlined />}
                      className="bg-gradient-to-br from-emerald-500 to-emerald-600"
                    />
                  </div>
                </Dropdown>
              </Space>
            </div>
          </header>
          <div className="flex flex-col overflow-y-scroll h-[calc(100dvh-80px)] md:h-[100dvh]">
            {!!paymentAlert && (
              <Alert
                style={{ marginTop: 12 }}
                message={`Your Payment of $${paymentAlert?.amount} is due`}
                type="error"
                className="mx-6"
                showIcon
                action={
                  <Space>
                    <Button
                      loading={loading}
                      danger
                      size="middle"
                      type="primary"
                      onClick={onPay}
                    >
                      Pay
                    </Button>
                  </Space>
                }
              />
            )}
            <Outlet />
          </div>
        </div>
      </div>

      <ModalDashboardSettings
        open={showThemeDrawer}
        onClose={() => {
          setShowThemeDrawer(false);
          cancelPreview();
        }}
        savedKey={savedKey}
        selectedKey={presetKey}
        mode={colorMode}
        isSaving={isSavingTheme}
        onPreview={setPreview}
        // close only once the save lands, so a failure keeps the picker open
        onSave={(key) =>
          saveTheme(key, { onSuccess: () => setShowThemeDrawer(false) })
        }
        onReset={() =>
          resetTheme({ onSuccess: () => setShowThemeDrawer(false) })
        }
      />

      <BarcodeReader
        onError={(code: string) => handleScan(code, "by error")}
        onScan={(code: string) => handleScan(code, "by scan")}
        // onKeyDetact={handleEnter}
        // onKeyDetect={handleOnKeyDetect}
      />
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  // Map state properties to props here if needed
  profileData: state.user.profileData,
  roleType: state.user.roleType,
  store: state.user.profileData?.store ?? {},
  menu: state.user.profileData?.menu ?? {},
  storeId: state.user?.profileData?.data?.id,
});

const mapDispatchToProps = (dispatch: any) => ({
  // login: (data: any) => dispatch(getProfile(data)),
  logout: () => dispatch(userActions.logout()),
  fetchLookup: (reqData: any) =>
    dispatch(fetchData(PATH_API_MANAGE, "lookup", reqData, "lookup")),
});

const connector = connect(mapStateToProps, mapDispatchToProps);

// Define the props that connect will inject into the component
type PropsFromRedux = ConnectedProps<typeof connector>;

// Connect the component to Redux
export default connector(CorePage);
